import { useTraduccion, type Clave } from '@tallerya/i18n';
import {
  IconoAgenda,
  IconoCaja,
  IconoChevronAbajo,
  IconoDashboard,
  IconoEngrane,
  IconoGafete,
  IconoPersona,
  IconoPortapapeles,
  IconoRecibo,
  IconoReporte,
  IconoSalir,
  IconoVehiculo,
} from '@tallerya/ui';
import iconoMarca from '@tallerya/ui/marca/icono-invertido.svg';
import { useId, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { NavLink, useLocation } from 'react-router';
import { usePermisos } from '../permisos/usePermiso';

// El menú lateral del admin: «Menú lateral» del Figma, 233 px sobre el guinda
// de la marca.

interface Destino {
  clave: Clave;
  /** Los destinos de primer nivel llevan ícono; los de adentro de un grupo, no. */
  icono?: ReactNode;
  /** Sin ruta ni hijos, el destino todavía no tiene pantalla. */
  ruta?: string;
  /** Un grupo que se despliega, como Configuraciones. */
  hijos?: readonly Destino[];
  /** El permiso sin el cual el destino no se muestra. Sin esto, se muestra siempre. */
  permiso?: string;
}

/**
 * Los destinos, en el orden del Figma. **Cuando llega una pantalla, se le pone
 * la ruta acá y nada más.**
 *
 * **Un destino con `permiso` se oculta a quien no lo tiene; uno sin `permiso`
 * se muestra siempre.** La regla de antes era mostrar todos y dejar que la
 * `Guardia` dijera «No tienes permiso»; #104 pidió ocultar Clientes, y desde ahí
 * cada pantalla decide al darle su ruta: con `permiso`, el que también lleva su
 * `Guardia` en `App.tsx`. Las que aún no lo declaran siguen con la regla vieja.
 *
 * Ocultar no es proteger: la `Guardia` sigue en la ruta, y la que vale es la de
 * la API (ver `usePermiso.ts`). Mientras los permisos cargan, el destino no se
 * muestra: aparecer y desaparecer es peor que llegar un instante después.
 */
const DESTINOS: readonly Destino[] = [
  { clave: 'admin.nav.inicio', icono: <IconoDashboard />, ruta: '/' },
  {
    clave: 'admin.nav.catalogo',
    icono: <IconoCaja />,
    ruta: '/productos-servicios',
    permiso: 'catalog.edit',
  },
  { clave: 'admin.nav.clientes', icono: <IconoPersona />, ruta: '/clientes', permiso: 'customer.edit' },
  // No está en el Figma: se agregó con #119 para buscar un carro por la placa
  // sin saber de quién es.
  { clave: 'admin.nav.activos', icono: <IconoVehiculo />, ruta: '/activos' },
  { clave: 'admin.nav.personal', icono: <IconoGafete />, ruta: '/personal', permiso: 'staff.manage' },
  { clave: 'admin.nav.ordenes', icono: <IconoPortapapeles />, ruta: '/ordenes', permiso: 'order.view' },
  // No está en el Figma: el listado de la caja (#144), solo para quien paga y completa (#170).
  { clave: 'admin.nav.gastos', icono: <IconoRecibo />, ruta: '/gastos', permiso: 'expense.edit' },
  { clave: 'admin.nav.agendas', icono: <IconoAgenda /> },
  { clave: 'admin.nav.reportes', icono: <IconoReporte /> },
  {
    clave: 'admin.nav.configuracion',
    icono: <IconoEngrane />,
    hijos: [
      { clave: 'admin.nav.sucursales', ruta: '/sucursales' },
      { clave: 'admin.nav.marcas', ruta: '/marcas' },
      { clave: 'admin.nav.catalogos', ruta: '/catalogos' },
      { clave: 'admin.nav.estadosDeOrden', ruta: '/estados-de-orden', permiso: 'settings.edit' },
      { clave: 'admin.nav.proveedores', ruta: '/proveedores', permiso: 'supplier.edit' },
    ],
  },
];

interface Props {
  id: string;
  /** Se llama al elegir un destino: en celular, cierra el cajón. */
  alElegir: () => void;
}

export function MenuLateral({ id, alElegir }: Props) {
  const { t } = useTraduccion();
  const auth = useAuth();
  const { puede } = usePermisos();
  // `puede` responde `false` mientras los permisos cargan: el destino llega después.
  const visible = (destino: Destino) => !destino.permiso || puede(destino.permiso);

  return (
    <nav id={id} className="menu-lateral" aria-label={t('admin.layout.menu')}>
      {/* El cuadro TY blanco: el guinda no se vería sobre el guinda del menú (#122).
          Sin texto alternativo porque el nombre va al lado. */}
      <p className="menu-lateral__marca">
        <img className="menu-lateral__logo" src={iconoMarca} alt="" />
        {t('app.marca')}
      </p>

      <ul className="menu-lateral__lista">
        {DESTINOS.filter(visible).map((destino) => (
          <li key={destino.clave}>
            {destino.hijos ? (
              // Los de adentro también se ocultan a quien no tiene su permiso.
              <Grupo destino={destino} hijos={destino.hijos.filter(visible)} alElegir={alElegir} />
            ) : (
              <Item destino={destino} alElegir={alElegir} />
            )}
          </li>
        ))}
      </ul>

      {auth.isAuthenticated && (
        <button
          type="button"
          className="menu-lateral__item menu-lateral__salir"
          // En Keycloak, no solo acá: ver `Sesion.tsx`.
          onClick={() => void auth.signoutRedirect()}
        >
          <IconoSalir />
          {t('admin.nav.cerrarSesion')}
        </button>
      )}
    </nav>
  );
}

function Item({ destino, alElegir }: { destino: Destino; alElegir: () => void }) {
  const { t } = useTraduccion();
  const clase = `menu-lateral__item${destino.icono ? '' : ' menu-lateral__item--hijo'}`;

  if (destino.ruta) {
    // Sin `end`, `/sucursales/nueva` deja marcado su destino. El inicio sí lo
    // lleva: si no, `/` quedaría marcado siempre.
    return (
      <NavLink to={destino.ruta} end={destino.ruta === '/'} className={clase} onClick={alElegir}>
        {destino.icono}
        {t(destino.clave)}
      </NavLink>
    );
  }

  // Un destino sin pantalla no lleva a ningún lado: quien lo toca se queda
  // donde está, nunca en «No encontrada», y en celular el cajón sigue abierto.
  return (
    <button type="button" className={clase} aria-disabled="true" title={t('admin.nav.pronto')}>
      {destino.icono}
      {t(destino.clave)}
      <span className="solo-lectores">{t('admin.nav.pronto')}</span>
    </button>
  );
}

/**
 * Un destino que se despliega. Arranca abierto si la pantalla actual es de
 * adentro —o se abre en cuanto lo es, si sus hijos llegan tarde—: quien entra
 * directo a `/sucursales` tiene que ver dónde está parado.
 */
function Grupo({
  destino,
  hijos,
  alElegir,
}: {
  destino: Destino;
  hijos: readonly Destino[];
  alElegir: () => void;
}) {
  const { t } = useTraduccion();
  const { pathname } = useLocation();
  const idLista = useId();
  const adentro = hijos.some((h) => h.ruta && pathname.startsWith(h.ruta));
  const [abierto, setAbierto] = useState(adentro);
  // Los hijos con permiso llegan después de dibujar: quien entra directo a
  // `/proveedores` no tiene a Proveedores entre los hijos en el primer dibujado, y
  // `adentro` nace falso. La primera vez que pasa a verdadero se abre el grupo; de
  // ahí en adelante manda la persona, como siempre.
  const [yaEstuvoAdentro, setYaEstuvoAdentro] = useState(adentro);
  if (adentro && !yaEstuvoAdentro) {
    setYaEstuvoAdentro(true);
    setAbierto(true);
  }

  return (
    <>
      <button
        type="button"
        className={`menu-lateral__item${adentro ? ' menu-lateral__item--adentro' : ''}`}
        aria-expanded={abierto}
        aria-controls={idLista}
        onClick={() => setAbierto((a) => !a)}
      >
        {destino.icono}
        {t(destino.clave)}
        <span className={`menu-lateral__flecha${abierto ? ' menu-lateral__flecha--abierta' : ''}`}>
          <IconoChevronAbajo />
        </span>
      </button>
      <ul id={idLista} className="menu-lateral__lista" hidden={!abierto}>
        {hijos.map((hijo) => (
          <li key={hijo.clave}>
            <Item destino={hijo} alElegir={alElegir} />
          </li>
        ))}
      </ul>
    </>
  );
}
